import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/**
 * Long-form Thai guide: Apostille vs consular legalisation, compared by
 * destination country. Deterministic, hand-written, SSR-only.
 */
const TITLE = "Apostille กับ Legalization ต่างกันอย่างไร — เทียบรายประเทศ";
const DESC =
  "เปรียบเทียบเส้นทางรับรองเอกสารไทยไปใช้ต่างประเทศแบบรายประเทศ ทั้งระบบ Apostille และการรับรองผ่านสถานทูต พร้อมลำดับขั้นตอนจริงและข้อควรระวัง";
const URL = "/blog/apostille-vs-legalization-by-country";
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "ตอนนี้ประเทศไทยออก Apostille ได้แล้วหรือยัง",
    answer:
      "ยังไม่ได้ ประเทศไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญา Apostille ค.ศ. 1961 เมื่อวันที่ 30 มิถุนายน 2569 และอนุสัญญาจะมีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 ก่อนวันดังกล่าวเอกสารไทยยังต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุล และรับรองที่สถานทูตปลายทางตามที่แต่ละสถานทูตกำหนด",
  },
  {
    question: "Apostille แทนคำแปลได้หรือไม่",
    answer:
      "ไม่ได้ Apostille รับรองเฉพาะลายมือชื่อ ตราประทับ และอำนาจหน้าที่ของเจ้าหน้าที่ผู้ออกเอกสารเท่านั้น ไม่ได้รับรองเนื้อหาและไม่ได้แปลเอกสาร หน่วยงานปลายทางส่วนใหญ่ยังกำหนดให้ยื่นคำแปลตามกฎของประเทศนั้นควบคู่ไปด้วย",
  },
  {
    question: "ประเทศที่ไม่ได้เป็นภาคีอนุสัญญาต้องทำอย่างไร",
    answer:
      "ต้องใช้เส้นทาง Legalization เต็มรูปแบบ คือรับรองต้นทาง (หน่วยงานผู้ออกเอกสารหรือทนายผู้ทำคำรับรองลายมือชื่อ) แล้วรับรองนิติกรณ์ที่กรมการกงสุล จากนั้นนำไปรับรองที่สถานทูตหรือสถานกงสุลของประเทศปลายทางในประเทศไทย",
  },
  {
    question: "เอกสารที่รับรองแล้วมีอายุการใช้งานหรือไม่",
    answer:
      "ตัวการรับรองไม่มีวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางหลายแห่งกำหนดว่าเอกสารทะเบียนราษฎรหรือหนังสือรับรองต้องออกใหม่ภายใน 3-6 เดือนก่อนยื่น จึงควรตรวจเงื่อนไขของหน่วยงานปลายทางก่อนเริ่มกระบวนการ",
  },
  {
    question: "ต้องรับรองต้นฉบับหรือสำเนา",
    answer:
      "โดยทั่วไปกรมการกงสุลรับรองจากต้นฉบับหรือสำเนาที่หน่วยงานผู้ออกเอกสารรับรองสำเนาถูกต้อง สำเนาที่ถ่ายเองโดยไม่มีการรับรองมักถูกปฏิเสธตั้งแต่จุดรับเรื่อง",
  },
  {
    question: "ประเทศที่เป็นภาคีแล้ว จะยังต้องไปสถานทูตอีกหรือไม่",
    answer:
      "เมื่ออนุสัญญามีผลกับประเทศไทยและประเทศปลายทางไม่ได้คัดค้านการเข้าเป็นภาคี ตามหลักการจะใช้ Apostille ใบเดียวโดยไม่ต้องผ่านสถานทูตอีก แต่ควรตรวจสอบตารางสถานะของ HCCH และประกาศของหน่วยงานปลายทางเป็นรายกรณีก่อนตัดขั้นตอนสถานทูตออก",
  },
];

export const Route = createFileRoute("/blog/apostille-vs-legalization-by-country")({
  component: ApostilleVsLegalizationGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "th") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "apostille กับ legalization ต่างกันอย่างไร, รับรองเอกสารไปต่างประเทศ, นิติกรณ์กรมการกงสุล, รับรองเอกสารสถานทูต, apostille ประเทศไทย",
        },
      ],
      links: canonicalAndHreflang(URL),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บทความ", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const ROUTES: Array<{ group: string; countries: string; path: string; note: string }> = [
  {
    group: "ออสเตรเลีย นิวซีแลนด์",
    countries: "Australia, New Zealand",
    path: "ทนาย Notary / หน่วยงานผู้ออกเอกสาร → กรมการกงสุล → (บางกรณี) สถานทูต",
    note: "หน่วยงานตรวจคนเข้าเมืองออสเตรเลียเน้นคำแปลโดยนักแปล NAATI มากกว่าตัวตราประทับสถานทูต",
  },
  {
    group: "สหภาพยุโรป",
    countries: "เยอรมนี ฝรั่งเศส อิตาลี สเปน เนเธอร์แลนด์ ฯลฯ",
    path: "หน่วยงานผู้ออกเอกสาร → คำแปล → กรมการกงสุล → สถานทูตประเทศปลายทาง",
    note: "หลายสถานทูตกำหนดให้แปลโดยผู้แปลในบัญชีของสถานทูตเอง และนัดหมายล่วงหน้า",
  },
  {
    group: "ญี่ปุ่น เกาหลีใต้",
    countries: "Japan, South Korea",
    path: "หน่วยงานผู้ออกเอกสาร → คำแปล → กรมการกงสุล → สถานทูต",
    note: "เอกสารทะเบียนราษฎรมักต้องคัดใหม่ และต้องตรงกับการสะกดชื่อในหนังสือเดินทาง",
  },
  {
    group: "จีน",
    countries: "China",
    path: "หน่วยงานผู้ออกเอกสาร → คำแปล → กรมการกงสุล → สถานทูตจีน",
    note: "จีนเป็นภาคีอนุสัญญาแล้ว แต่ระหว่างที่อนุสัญญายังไม่มีผลกับไทย เอกสารไทยยังใช้เส้นทางสถานทูต",
  },
  {
    group: "สหรัฐอเมริกา แคนาดา",
    countries: "USA, Canada",
    path: "ทนาย Notary → กรมการกงสุล → (แล้วแต่หน่วยงานปลายทาง)",
    note: "หน่วยงานมลรัฐ/มณฑลกำหนดเงื่อนไขต่างกัน ควรขอเงื่อนไขเป็นลายลักษณ์อักษรจากผู้รับเอกสารก่อน",
  },
  {
    group: "ตะวันออกกลาง",
    countries: "UAE, ซาอุดีอาระเบีย, กาตาร์",
    path: "หน่วยงานผู้ออกเอกสาร → คำแปล → กรมการกงสุล → สถานทูต → (บางประเทศ) กระทรวงต่างประเทศปลายทาง",
    note: "ขั้นตอนยาวที่สุด ควรเผื่อเวลาและตรวจรูปแบบคำแปลอาหรับตามที่สถานทูตกำหนด",
  },
];

function ApostilleVsLegalizationGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:underline">หน้าแรก</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:underline">บทความ</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span>Apostille vs Legalization</span>
      </nav>

      <h1>Apostille กับ Legalization ต่างกันอย่างไร — เทียบรายประเทศ</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="th" />
      <p className="speakable-summary lead answer-summary">
        Apostille คือใบรับรองมาตรฐานใบเดียวตามอนุสัญญากรุงเฮก ค.ศ. 1961 ที่ประเทศภาคีรับรองกันเอง
        ส่วน Legalization คือการรับรองเป็นทอด ๆ ตั้งแต่หน่วยงานต้นทาง กรมการกงสุล
        ไปจนถึงสถานทูตของประเทศปลายทาง ปัจจุบันเอกสารที่ออกในประเทศไทยยังใช้เส้นทาง Legalization
        เนื่องจากอนุสัญญาจะมีผลกับประเทศไทยในวันที่ 28 กุมภาพันธ์ 2570 บทความนี้เทียบเส้นทางจริง
        รายกลุ่มประเทศ พร้อมลำดับขั้นตอนและจุดที่ทำให้เอกสารถูกตีกลับบ่อยที่สุด
      </p>

      <h2>1. นิยามที่ต้องแยกให้ชัดก่อน</h2>
      <p>
        คำสามคำที่คนมักใช้ปนกันคือ <strong>รับรองลายมือชื่อโดยทนาย (Notarial Services Attorney)</strong>,{" "}
        <strong>นิติกรณ์ (Legalization) ของกรมการกงสุล</strong> และ <strong>Apostille</strong>
        ทั้งสามอย่างอยู่คนละชั้นของกระบวนการ ไม่ใช่ทางเลือกแทนกัน
      </p>
      <ul>
        <li>
          <strong>ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร</strong> รับรองว่าผู้ลงนามลงนามต่อหน้าจริง
          หรือรับรองสำเนาถูกต้องของเอกสารที่ไม่ใช่เอกสารราชการ เช่น หนังสือมอบอำนาจ คำให้การ (affidavit)
          หนังสือยินยอมของบิดามารดา สัญญาส่วนบุคคล — ชั้นนี้เป็น "ต้นทาง" ของเอกสารเอกชน
        </li>
        <li>
          <strong>นิติกรณ์กรมการกงสุล</strong> รับรองว่าลายมือชื่อและตราของผู้รับรองชั้นก่อนหน้า
          (นายทะเบียน เจ้าพนักงาน หรือทนาย) เป็นของจริงและมีอำนาจจริง — ชั้นนี้คือ "รัฐไทยยืนยัน"
        </li>
        <li>
          <strong>Apostille</strong> คือใบรับรองรูปแบบเดียวกันทั่วโลกที่ทำหน้าที่แทนการรับรองซ้ำที่สถานทูต
          ระหว่างประเทศภาคีด้วยกัน — ชั้นนี้ยัง <em>ไม่</em> เปิดใช้สำหรับเอกสารที่ออกในประเทศไทย
        </li>
      </ul>

      <h2>2. เส้นทางปัจจุบันของเอกสารไทย</h2>
      <p>
        ณ วันที่เผยแพร่บทความนี้ เอกสารที่ออกในประเทศไทยและจะนำไปใช้ต่างประเทศ ใช้ลำดับดังนี้
      </p>
      <ol>
        <li>
          <strong>จัดเตรียมต้นฉบับ</strong> เอกสารทะเบียนราษฎร เช่น สูติบัตร ทะเบียนสมรส ทะเบียนหย่า
          มรณบัตร ทะเบียนบ้าน ให้คัดรับรองใหม่จากสำนักงานเขตหรือที่ว่าการอำเภอ
          ส่วนเอกสารเอกชนให้ลงนามต่อหน้าทนายผู้ทำคำรับรองลายมือชื่อ
        </li>
        <li>
          <strong>แปลเป็นภาษาที่ปลายทางกำหนด</strong> โดยผู้แปลที่กรมการกงสุลรับ
          และรูปแบบคำแปลต้องตรงตามแบบที่กองสัญชาติและนิติกรณ์กำหนด
        </li>
        <li>
          <strong>ยื่นนิติกรณ์ที่กรมการกงสุล</strong> ถนนแจ้งวัฒนะ หรือสำนักงานสาขา
          พร้อมต้นฉบับ คำแปล แบบคำร้อง และบัตรประชาชน/หนังสือเดินทางของผู้ยื่น
        </li>
        <li>
          <strong>รับรองที่สถานทูตประเทศปลายทาง</strong> เฉพาะประเทศที่ยังกำหนดขั้นตอนนี้
          ซึ่งแต่ละแห่งมีเงื่อนไขเรื่องการนัดหมาย จำนวนชุด และผู้แปลที่ยอมรับต่างกัน
        </li>
      </ol>
      <p className="text-sm">
        แหล่งอ้างอิง:{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          กรมการกงสุล กระทรวงการต่างประเทศ — งานนิติกรณ์
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/status-table/?cid=41" target="_blank" rel="noreferrer">
          ตารางสถานะภาคีอนุสัญญา Apostille (HCCH)
        </a>{" "}
        ·{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          สภาทนายความในพระบรมราชูปถัมภ์
        </a>
      </p>

      <h2>3. ตารางเทียบรายกลุ่มประเทศ</h2>
      <p>
        ตารางนี้สรุป "ลำดับที่ใช้จริงในทางปฏิบัติ" ไม่ใช่ข้อกำหนดทางกฎหมายของประเทศปลายทาง
        เงื่อนไขของแต่ละหน่วยงานอาจต่างกันแม้อยู่ในประเทศเดียวกัน จึงควรขอเงื่อนไขจากผู้รับเอกสารเป็นลายลักษณ์อักษรก่อนเริ่ม
      </p>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">กลุ่มประเทศ</th>
              <th className="p-2 text-left">ลำดับขั้นตอน</th>
              <th className="p-2 text-left">ข้อสังเกต</th>
            </tr>
          </thead>
          <tbody>
            {ROUTES.map((r) => (
              <tr key={r.group} className="border-b align-top">
                <td className="p-2">
                  <strong>{r.group}</strong>
                  <div className="text-muted-foreground">{r.countries}</div>
                </td>
                <td className="p-2">{r.path}</td>
                <td className="p-2 text-muted-foreground">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>4. เมื่ออนุสัญญามีผลกับประเทศไทยแล้ว จะเปลี่ยนอะไร</h2>
      <p>
        สิ่งที่เปลี่ยนคือ "จำนวนชั้นการรับรอง" ไม่ใช่ "คุณภาพของเอกสาร" เมื่ออนุสัญญามีผล
        เอกสารที่จะไปประเทศภาคีซึ่งไม่ได้คัดค้านการเข้าเป็นภาคีของไทย จะใช้ Apostille ใบเดียว
        แทนการไปรับรองที่สถานทูตอีกทอดหนึ่ง แต่สิ่งที่ยังเหมือนเดิมมีสามข้อ
      </p>
      <ul>
        <li>เอกสารต้นทางยังต้องถูกต้อง คัดใหม่ในกรอบเวลาที่ปลายทางกำหนด และชื่อสะกดตรงหนังสือเดินทาง</li>
        <li>คำแปลยังจำเป็น และปลายทางยังเป็นผู้กำหนดว่าใครแปลได้</li>
        <li>เอกสารเอกชนยังต้องผ่านการรับรองลายมือชื่อโดยทนายก่อน จึงจะเข้าสู่ชั้นรับรองของรัฐได้</li>
      </ul>
      <p>
        ในทางปฏิบัติ ผู้ที่มีกำหนดยื่นเอกสารก่อนวันที่ 28 กุมภาพันธ์ 2570 จึงไม่ควรรอ Apostille
        แต่ควรเดินเส้นทางเดิมให้จบ เพราะเอกสารที่รับรองครบตามเส้นทางเดิมยังใช้ได้ต่อไป
      </p>

      <h2>5. ข้อผิดพลาดที่ทำให้เอกสารถูกตีกลับบ่อยที่สุด</h2>
      <ol>
        <li>
          <strong>สะกดชื่อไม่ตรงหนังสือเดินทาง</strong> เป็นสาเหตุอันดับหนึ่ง โดยเฉพาะการถอดอักษรไทยเป็นอังกฤษ
          ที่แต่ละฉบับสะกดไม่เหมือนกัน ควรยึดหนังสือเดินทางเป็นมาตรฐานเดียวทุกฉบับ
        </li>
        <li>
          <strong>ใช้สำเนาที่ไม่มีตรารับรองจากหน่วยงานผู้ออก</strong> ทำให้ตกตั้งแต่จุดรับเรื่อง
        </li>
        <li>
          <strong>เอกสารเก่าเกินกรอบเวลา</strong> เช่น ทะเบียนบ้านหรือทะเบียนสมรสที่คัดไว้เกินหกเดือน
        </li>
        <li>
          <strong>คำแปลผิดรูปแบบ</strong> เช่น ไม่มีคำรับรองผู้แปล ไม่ระบุเลขที่เอกสารต้นฉบับ
          หรือแปลชื่อหน่วยงานไม่ตรงกับที่หน่วยงานปลายทางคุ้นเคย
        </li>
        <li>
          <strong>เรียงลำดับผิด</strong> เช่น นำไปสถานทูตก่อนผ่านนิติกรณ์ ทำให้ต้องเริ่มใหม่ทั้งชุด
        </li>
        <li>
          <strong>จำนวนชุดไม่พอ</strong> หลายหน่วยงานเก็บต้นฉบับที่รับรองแล้วไว้ ทำให้ผู้ยื่นไม่เหลือชุดสำหรับใช้ในขั้นตอนถัดไป
        </li>
      </ol>

      <h2>6. วางแผนเวลาอย่างไรให้ทัน</h2>
      <p>
        การวางแผนที่ปลอดภัยคือ ไล่ย้อนจากวันนัดของหน่วยงานปลายทาง แล้วเผื่อเวลาให้แต่ละชั้น
        โดยเฉพาะขั้นตอนสถานทูตซึ่งมักถูกจำกัดด้วยคิวนัดหมาย ไม่ใช่ด้วยความเร็วในการทำงาน
        เอกสารชุดใหญ่ เช่น ชุดวีซ่าครอบครัวหรือชุดจดทะเบียนบริษัทในต่างประเทศ
        ควรแยกเป็นล็อตเพื่อไม่ให้เอกสารทั้งชุดค้างเพราะฉบับเดียวมีปัญหา
      </p>
      <p>
        เราแนะนำให้ตรวจสามอย่างก่อนเริ่มเสมอ คือ (1) หน่วยงานปลายทางต้องการฉบับภาษาอะไร
        (2) ยอมรับผู้แปลกลุ่มใด และ (3) ต้องการเอกสารที่ออกใหม่ภายในกี่เดือน
        คำตอบสามข้อนี้กำหนดเส้นทางทั้งหมด และช่วยเลี่ยงการทำงานซ้ำ
      </p>

      <h2>7. บริการที่เกี่ยวข้อง</h2>
      <p>
        เราให้คำปรึกษาก่อนเริ่มงานทุกเคส ตรวจเอกสารล่วงหน้า วางลำดับตามประเทศปลายทาง
        และติดตามจนจบกระบวนการ ดูรายละเอียดที่{" "}
        <Link to="/services">บริการทั้งหมด</Link> · <Link to="/faq">คำถามที่พบบ่อย</Link> ·{" "}
        <Link to="/contact">ติดต่อเรา</Link> หรือโทร{" "}
        <a href="tel:+66948958999">094-895-8999</a> เพื่อให้ทีมงานประเมินเส้นทางเอกสารของคุณ
      </p>

      <section className="not-prose mt-12 border-t pt-8">
        <h2 className="text-2xl font-semibold mb-4">คำถามที่พบบ่อย</h2>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details key={f.question} className="rounded-lg border p-4">
              <summary className="font-medium cursor-pointer">{f.question}</summary>
              <p className="mt-2 text-muted-foreground faq-answer">{f.answer}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          ตรวจทานเนื้อหาโดยทีมทนายผู้ทำคำรับรองลายมือชื่อและเอกสารของสำนักงาน · ตรวจทานล่าสุด 10 สิงหาคม 2569
          · เนื้อหานี้เป็นข้อมูลทั่วไปเกี่ยวกับขั้นตอน ไม่ใช่คำแนะนำทางกฎหมายเฉพาะกรณี
        </p>
      </section>
    </article>
  );
}
